/**
 * «Премия» → «Запись бронь»: брони периода, по которым была запись, — кто, где и каким способом
 * записал клиента (звонок, переписка, ссылка на запись) и что с премией «Запись.Бронь».
 * Спека `docs/specs/2026-10-07-bonus-zapis-design.md`; числа — те же, что у
 * `prokat-runner billing-zapis`.
 */
import type { ReactNode } from "react";
import { Link } from "react-router";

import { money, type ZapisBonus, type ZapisMethod, type ZapisRow, type ZapisWhere } from "@/lib/bonus";

const dm = (at: string) => `${at.slice(8, 10)}.${at.slice(5, 7)}`;
const hm = (at: string) => `${dm(at)} ${at.slice(11, 16)}`;

const METHODS: { id: ZapisMethod; label: string; color: string; hint: string }[] = [
  { id: "call", label: "звонок", color: "var(--chart-1)", hint: "разговор сотрудника с клиентом за 45 минут до записи" },
  { id: "chat", label: "переписка", color: "var(--ok)", hint: "запись из «Чатов» или расширения, либо ответ клиенту в чате за 2 часа до записи" },
  { id: "link", label: "ссылка", color: "var(--chart-3)", hint: "клиент сам выбрал время по ссылке на запись" },
  { id: "other", label: "не определён", color: "var(--fg-faint)", hint: "запись в Bitrix: ни звонка, ни переписки рядом в наших данных нет" },
];
const METHOD = Object.fromEntries(METHODS.map((m) => [m.id, m])) as Record<ZapisMethod, (typeof METHODS)[number]>;
export const WHERE: Record<ZapisWhere, string> = {
  bitrix: "в Bitrix",
  chats: "в «Чатах»",
  ext: "в расширении",
  link: "клиент по ссылке",
  auto: "клиент сам",
  robot: "робот CRM",
};
export const CHANNEL: Record<string, string> = {
  whatsapp: "WhatsApp",
  telegram: "Telegram",
  max: "MAX",
  avito: "Авито",
  vk: "ВК",
  instagram: "Instagram",
};

function Tile({ label, value, hint, tone = "" }: { label: string; value: ReactNode; hint?: ReactNode; tone?: string }) {
  return (
    <div
      className="flex min-w-0 flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4"
      data-annot={`плитка «${label}»`}
    >
      <div className="text-[12px] text-fg-muted">{label}</div>
      <div className={`text-[26px] font-semibold leading-none tracking-[-0.02em] tabular-nums ${tone}`}>{value}</div>
      {hint && <div className="text-[12px] text-fg-faint">{hint}</div>}
    </div>
  );
}

function Dot({ id }: { id: ZapisMethod }) {
  return <span className="inline-block size-2 flex-none rounded-full" style={{ background: METHOD[id].color }} />;
}

/** Полоса «каким способом записали»: доли способов и подписи с числами. */
function Methods({ r }: { r: ZapisBonus }) {
  const shown = METHODS.filter((m) => r.methods[m.id] > 0);
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4" data-annot="способы записи">
      <div className="flex h-2.5 overflow-hidden rounded-full bg-neutral-bg">
        {shown.map((m) => (
          <div key={m.id} title={`${m.label}: ${r.methods[m.id]}`} style={{ width: `${(r.methods[m.id] / r.count) * 100}%`, background: m.color }} />
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1.5 text-[12.5px]">
        {METHODS.map((m) => (
          <span key={m.id} className="inline-flex cursor-help items-center gap-1.5" title={m.hint}>
            <Dot id={m.id} />
            <span className="text-fg-muted">{m.label}</span>
            <b className="tabular-nums">{r.methods[m.id]}</b>
            <span className="tabular-nums text-fg-faint">{r.count ? Math.round((r.methods[m.id] / r.count) * 100) : 0}%</span>
          </span>
        ))}
      </div>
      <p className="mt-3 text-[12.5px] text-fg-muted">
        {r.amount} ₽ (до 07.10.26 — 60 ₽) получает тот, кто последним до брони записал клиента, — звонком, перепиской или ссылкой на запись. Запись из
        «Чатов», расширения Chrome и по вашей ссылке оплачивается так же, как запись после звонка.
      </p>
    </section>
  );
}

function Premium({ x, r }: { x: ZapisRow; r: ZapisBonus }) {
  if (x.billing) {
    return (
      <span title={`проводка ${x.billing.id}`}>
        <b className="tabular-nums">{money(x.billing.amount)} ₽</b>
        {x.billing.user_id !== x.user_id && <span className="ml-1.5 text-fg-muted">{x.billing.user_name}</span>}
        {x.billing.own && <span className="ml-1.5 rounded bg-ok-bg px-1 text-[11.5px] text-ok">prokat</span>}
      </span>
    );
  }
  if (x.status === "due") {
    return (
      <span className="text-warn" title={r.auto ? "начислит фон prokat в течение 10 минут" : "фон начисления prokat выключен"}>
        к начислению {r.amount} ₽
      </span>
    );
  }
  return <span className="text-fg-faint" title="клиент записался сам — сотрудник его не записывал">нет — клиент сам</span>;
}

export function ZapisReport({ r, pick }: { r: ZapisBonus; pick?: ((id: number) => void) | undefined }) {
  const messaging = r.methods.chat + r.methods.link;
  const cell = "whitespace-nowrap px-2.5 py-1.5";
  const num = `${cell} text-right tabular-nums`;
  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          label="Записей с бронью"
          value={r.count}
          hint={
            r.walk_in === null
              ? "клиент записан и забронировал"
              : `ещё ${r.walk_in} броней — клиент пришёл без записи` + (r.self ? `, ${r.self} — записался сам` : "")
          }
        />
        <Tile label="Начислено" value={<>{money(r.paid)} ₽</>} hint={`${r.amount} ₽ за запись`} />
        <Tile
          label="К начислению"
          value={<>{money(r.due)} ₽</>}
          tone={r.due ? "text-warn" : ""}
          hint={
            r.due_count === 0
              ? "записей без премии нет"
              : r.auto
                ? `${r.due_count} — начислит фон в течение 10 минут`
                : `${r.due_count} — фон начисления выключен`
          }
        />
        <Tile
          label="Перепиской и ссылкой"
          value={r.count ? `${Math.round((messaging / r.count) * 100)}%` : "—"}
          hint={`${messaging} из ${r.count} записей — без звонка`}
        />
      </div>
      {r.count === 0 ? (
        <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4 text-[12.5px] text-fg-muted">
          За этот период броней с записью нет.
        </div>
      ) : (
        <>
          <Methods r={r} />
          {r.people.length > 1 && (
            <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="записи по сотрудникам">
              <div className="overflow-x-auto">
                <table className="w-full text-[12.5px]">
                  <thead className="text-[11.5px] text-fg-faint">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium">Сотрудник</th>
                      <th className={`${num} font-medium`}>Записей</th>
                      {METHODS.map((m) => (
                        <th key={m.id} className={`${num} cursor-help font-medium`} title={m.hint}>
                          <span className="inline-flex items-center gap-1.5">
                            <Dot id={m.id} />
                            {m.label}
                          </span>
                        </th>
                      ))}
                      <th className={`${num} font-medium`}>Начислено</th>
                      <th className={`${num} pr-4 font-medium`} title="записи без проводки: начислит фон prokat">
                        Ждёт
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.people.map((p) => (
                      <tr
                        key={p.user_id}
                        className={`border-t border-border ${pick ? "cursor-pointer hover:bg-bg-hover" : ""}`}
                        title={pick ? "Показать записи сотрудника" : undefined}
                        onClick={pick ? () => pick(p.user_id) : undefined}
                        data-annot={`сотрудник ${p.user_id}`}
                      >
                        <td className="whitespace-nowrap px-4 py-1.5">{p.name}</td>
                        <td className={`${num} font-semibold`}>{p.count}</td>
                        {METHODS.map((m) => (
                          <td key={m.id} className={num}>
                            {p.methods[m.id] || <span className="text-fg-faint">·</span>}
                          </td>
                        ))}
                        <td className={num}>{money(p.paid)}</td>
                        <td className={`${num} pr-4 ${p.due ? "text-warn" : "text-fg-faint"}`}>{p.due ? money(p.due) : "·"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
          <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="записи">
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead className="text-left text-[11.5px] text-fg-faint">
                  <tr>
                    <th className="px-4 py-2 font-medium">Бронь</th>
                    <th className={`${cell} font-medium`}>Сделка</th>
                    <th className={`${cell} font-medium`}>Записал</th>
                    <th className={`${cell} font-medium`}>Когда</th>
                    <th className={`${cell} font-medium`}>Где</th>
                    <th className={`${cell} font-medium`}>Способ</th>
                    <th className={`${cell} pr-4 font-medium`}>Премия</th>
                  </tr>
                </thead>
                <tbody>
                  {r.rows.map((x) => (
                    <tr key={x.deal_id} className="border-t border-border" data-annot={`запись сделки ${x.deal_id}`}>
                      <td className="whitespace-nowrap px-4 py-1.5 tabular-nums text-fg-muted">{hm(x.bron_at)}</td>
                      <td className={cell}>
                        <Link to={`/deals/${x.deal_id}`} className="font-mono underline" title="карточка сделки">
                          {x.deal_id}
                        </Link>
                      </td>
                      <td className={cell}>{x.user_name ?? <span className="text-fg-faint">—</span>}</td>
                      <td className={`${cell} tabular-nums text-fg-muted`}>{hm(x.zapis_at)}</td>
                      <td className={`${cell} text-fg-muted`}>{WHERE[x.where]}</td>
                      <td className={cell}>
                        <span className="inline-flex items-center gap-1.5" title={METHOD[x.method].hint}>
                          <Dot id={x.method} />
                          {METHOD[x.method].label}
                          {x.channel && <span className="text-fg-muted">{CHANNEL[x.channel] ?? x.channel}</span>}
                        </span>
                        {x.call_at && x.method !== "call" && (
                          <span className="ml-2 text-[11.5px] text-fg-faint" title="разговор с клиентом незадолго до записи">
                            + звонок {x.call_at.slice(11, 16)}
                          </span>
                        )}
                      </td>
                      <td className={`${cell} pr-4`}>
                        <Premium x={x} r={r} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </>
  );
}
